'use client';
import React, { FC, useMemo, useState } from 'react';
import {
  TypeMediaCollectionPlayerFields,
  TypeImage,
} from '@/core/generated-types';
import {
  Button,
  HeaderText,
  MediaCollectionChapter,
  MediaCollectionSupportingContent,
  RichText,
  Tabs,
} from '@/components';
import {
  ExpandedTabDisplayTypes,
  NavigationDisplayTypes,
} from '@/components/common/Tabs/Tabs';
import { Image } from '@/components/ui/Image';
import { HeaderSize } from '@/components/ui/HeaderText';
import { Modal } from '@/components/ui/Modal';
import { BUTTON_SIZES, BUTTON_STYLES } from '@/components/ui/Buttons/utils';
import useMediaCollectionNavigation from '@/components/mediaCollection/MediaCollectionPlayer/useMediaCollectionNavigation';
import { TypeUnsignedMuxVideoAsset } from '@/components/mediaCollection/MediaCollectionVideo/utils';
import { interpolate } from '@/utils/translations';
import { Document } from '@contentful/rich-text-types';
import MediaCollectionVideoPreview from '../MediaCollectionVideo/MediaCollectionVideoPreview';

export const MediaCollectionPlayerPreview: FC<
  TypeMediaCollectionPlayerFields
> = ({
  mediaCollectionProduct,
  previewVideoDetails,
  previewVideo,
  previewVideoTitle,
  additionalSupport,
}) => {
  const [isCoursePreviewOpen, setIsCoursePreviewOpen] = useState(false);
  const mediaCollectionNavigation = useMediaCollectionNavigation(
    mediaCollectionProduct,
  );
  const tabs = useMemo(() => {
    return mediaCollectionProduct.fields.parts.map(
      (mediaCollectionPart, i) => ({
        title: interpolate(mediaCollectionPart.fields.titleWithSectionPrefix, {
          currentSection: i + 1,
          sectionCount: mediaCollectionProduct.fields.parts.length,
        }),
        onSelectTab: () =>
          mediaCollectionNavigation.navigateToPosition(
            mediaCollectionPart.sys.id,
          ),
        jsxContent: (
          <>
            {mediaCollectionPart.fields.chapters.map((chapter, i) => (
              <MediaCollectionChapter
                key={chapter.sys.id}
                chapter={chapter}
                isActiveChapter={
                  chapter.sys.id ===
                  mediaCollectionNavigation.navigationPosition.chapterId
                }
                isFirstChapter={i === 0}
                navigateToChapter={() =>
                  mediaCollectionNavigation.navigateToChapter(chapter.sys.id)
                }
                closeChapter={() =>
                  mediaCollectionNavigation.navigateToChapter('')
                }
                enablePreviewMode
              />
            ))}
          </>
        ),
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaCollectionProduct.fields.parts, mediaCollectionNavigation]);

  const { textHeading, textBody, image, cta } =
    previewVideoDetails?.fields || {};

  return (
    <div data-testid="media-collection-player-preview-container">
      <Modal
        isOpen={isCoursePreviewOpen}
        handleClose={() => setIsCoursePreviewOpen(false)}
      >
        {previewVideo && (
          <MediaCollectionVideoPreview
            muxVideo={previewVideo.fields as TypeUnsignedMuxVideoAsset}
            title={previewVideoTitle}
            isSigned={false}
          />
        )}
      </Modal>
      <div className="lg:container lg:mx-auto lg:grid lg:max-w-[1200px] lg:grid-cols-12 lg:gap-6">
        <div className="mx-4 text-left lg:col-span-4 lg:mx-0 lg:max-w-[450px]">
          <Image
            imageData={image as TypeImage}
            className="h-auto w-full"
            alt={image?.fields?.altText}
          />
          <HeaderText
            text={textHeading}
            size={HeaderSize.H3}
            align="left"
            classes="mt-3 mb-1"
          />
          {textBody && <RichText content={textBody as Document} />}
          <p className="text-left">
            {previewVideo && (
              <Button
                style={BUTTON_STYLES.link}
                size={BUTTON_SIZES.none}
                onClick={() => setIsCoursePreviewOpen(true)}
                classes="ml-2"
                ctaText={cta?.fields?.linkText}
                testId="open-media-collection-preview-video-modal"
              />
            )}
          </p>
        </div>
        <div
          className="mb-8 mt-6 lg:col-span-8 lg:mx-0 lg:mb-0 lg:mt-0 lg:flex lg:flex-col"
          data-testid="media-collection-player-navigation-container"
        >
          <div className="lg:relative lg:h-full">
            <div className="lg:absolute lg:left-0 lg:top-0 lg:flex lg:h-full lg:w-full lg:flex-col lg:overflow-y-scroll lg:overscroll-contain">
              <Tabs
                tabs={tabs}
                expandedTabDisplayType={ExpandedTabDisplayTypes.JSX}
                navigationDisplayType={NavigationDisplayTypes.Single}
              />
            </div>
          </div>
        </div>
      </div>
      {additionalSupport && (
        <div
          className="lg:mx-auto lg:mt-14 lg:max-w-[1200px] lg:border-t lg:border-charcoal-opacity-20"
          data-testid="media-collection-player-additional-support-container"
        >
          <div className="mt-14 grid grid-cols-1 gap-x-6 gap-y-10 md:grid-cols-2 lg:grid-cols-3">
            {additionalSupport.map((additionalSupportItem) => (
              <MediaCollectionSupportingContent
                key={additionalSupportItem.sys.id}
                {...additionalSupportItem.fields}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
